import { IAP_STORES, OFFER_CODE_MAX_LENGTH, OFFER_CODE_NOTE_MAX_LENGTH } from '@aysar/contracts';

import {
  type CustomFieldControl,
  type FieldDef,
} from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { ServerSelect } from '@/shared/components/selection/ServerSelect/ServerSelect';
import {
  useResolveDiscountRuleIds,
  useRuleOptionsQuery,
} from '@/shared/services/discount-rule-lookup/discount-rule-lookup.api';
import {
  useProductOptionsQuery,
  useResolveProductKeys,
} from '@/shared/services/subscription-product-lookup/subscription-product-lookup.api';

/** The module's translate function, narrowed the way `Translate` is elsewhere. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Mapping a discount rule to a store's offer code.
 *
 * ## Two field sets, not one with disabled controls
 *
 * `discountRuleId · productKey · store` are the mapping's identity and the
 * unique index over it, and the api's update contract omits all three: pointing
 * a code at a different rule is a *different mapping*. Showing them greyed out
 * on an edit would be three controls that look answerable and are not — so the
 * edit form is the three fields it can actually write.
 *
 * ## The code is never normalised
 *
 * Apple's offer codes are case-sensitive. There is no slug control and no
 * `transform` — what the operator pastes out of App Store Connect is what is
 * stored, exactly.
 */
export function buildOfferCodeCreateFields(t: Translate): FieldDef[] {
  return [
    {
      name: 'discountRuleId',
      kind: 'custom',
      label: t('offerCodes.form.rule'),
      description: t('offerCodes.form.ruleHint'),
      isRequired: true,
      colSpan: 'full',
      render: (control: CustomFieldControl) => (
        <ServerSelect
          id={control.id}
          value={readText(control.value)}
          isDisabled={control.isDisabled}
          isInvalid={control.isInvalid}
          isClearable
          placeholder={t('offerCodes.form.rulePlaceholder')}
          aria-describedby={control.describedById}
          useOptionsQuery={useRuleOptionsQuery}
          useResolveIds={useResolveDiscountRuleIds}
          onBlur={control.onBlur}
          onValueChange={(value) => {
            // `''` rather than `null`: the field is a required key, and an empty
            // string is what an unanswered `ServerSelect` reports.
            control.onChange(value ?? '');
          }}
        />
      ),
    },
    {
      name: 'productKey',
      kind: 'custom',
      label: t('offerCodes.form.plan'),
      description: t('offerCodes.form.planHint'),
      isRequired: true,
      colSpan: 'full',
      render: (control: CustomFieldControl) => (
        <ServerSelect
          id={control.id}
          value={readText(control.value)}
          isDisabled={control.isDisabled}
          isInvalid={control.isInvalid}
          isClearable
          placeholder={t('offerCodes.form.planPlaceholder')}
          aria-describedby={control.describedById}
          useOptionsQuery={useProductOptionsQuery}
          useResolveIds={useResolveProductKeys}
          onBlur={control.onBlur}
          onValueChange={(value) => {
            control.onChange(value ?? '');
          }}
        />
      ),
    },
    {
      name: 'store',
      kind: 'select',
      label: t('offerCodes.form.store'),
      description: t('offerCodes.form.storeHint'),
      isRequired: true,
      // A fixed coded enum of two, so a plain select rather than a picker (W6).
      options: IAP_STORES.map((store) => ({ value: store, label: t(`offerCodes.store.${store}`) })),
    },
    ...buildOfferCodeEditFields(t),
  ];
}

/** The three fields an existing mapping can actually change. */
export function buildOfferCodeEditFields(t: Translate): FieldDef[] {
  return [
    {
      name: 'offerCode',
      kind: 'text',
      label: t('offerCodes.form.code'),
      description: t('offerCodes.form.codeHint'),
      isRequired: true,
      maxLength: OFFER_CODE_MAX_LENGTH,
      // A plain text field, on `iosProductId`'s precedent — NOT a `slug`, which
      // would normalise a value Apple treats as case-sensitive. The LTR/monospace
      // treatment belongs in the table cell, which is where a code is actually
      // read back character by character against a store console.
    },
    {
      name: 'isActive',
      kind: 'switch',
      label: t('offerCodes.form.isActive'),
      description: t('offerCodes.form.isActiveHint'),
      colSpan: 'full',
    },
    {
      name: 'note',
      kind: 'textarea',
      label: t('offerCodes.form.note'),
      description: t('offerCodes.form.noteHint'),
      rows: 3,
      maxLength: OFFER_CODE_NOTE_MAX_LENGTH,
      colSpan: 'full',
      // An empty box is `null`, never `''`: the contract is `.nullable()` and
      // "no note" is a real answer rather than an unanswered field.
      isEmptyNull: true,
    },
  ];
}

/**
 * A `custom` control's value crosses the form as `unknown`, so it is narrowed
 * rather than asserted (U4). `''` reads as unanswered, which is what
 * `ServerSelect` wants as `null`.
 */
function readText(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}
